/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useWatch, useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

import FormReadOnlyField from "./FormReadOnlyField";
import FormEditableField from "./FormEditableField";
import FormReadOnlyMapping from "./FormReadOnlyMapping";
import FormDocumentField from "../Document";
import FormInfoField from "./FormInfo";

import { useDatagridContextValue } from "../context/datagridContext";
import {
  methodRefs,
  resetFormData,
  updateFormFields,
} from "../context/formSlice";
import {
  formConditional,
  getDatagridFormMethods,
  getWatchFieldName,
  conditionalDisable,
  disableCheck,
  disableInput,
  getDgWatchFieldName,
  getParsedValue,
  optionListChange,
  getConditionalRequiredConfig,
} from "../services/form.service";
import { showNotification } from "../../../features/core/slices/notification.slice";
import {
  getDocumentVisibility,
  evaluateConditionalRequired,
} from "../services/formDocument.service";
import { useReviewPageCheck } from "../context/reviewItemContext";
import useGetFormStateValues from "../hooks/useGetFormProps";

const statusList = ["Approved", "Submitted", "Paid", "Processing"];

function Field(props) {
  const dispatch = useDispatch();
  const { datagridFormState } = useDatagridContextValue();
  const isReviewContext = useReviewPageCheck();
  const {
    register: formRegister,
    control: formControl,
    getValues: formGetValues,
    setValue: formSetValue,
    trigger: formTrigger,
    getFieldState: formGetFieldState,
  } = useFormContext();
  const formStateValues = useGetFormStateValues();
  // to track the disabled state
  const [disabled, setDisabled] = useState(false);
  const [controllingFieldList, setControllingFieldList] = useState(undefined);
  const [fieldPropsCopy, setFieldPropsCopy] = useState(undefined);
  const formMethods = getDatagridFormMethods(
    methodRefs,
    datagridFormState,
    isReviewContext
  );

  const { fieldProps, index, length } = props;
  const { formData, formConfig, applicationData, dgSavedIds } = formStateValues;
  const { type, conditional, customConditional, properties } = fieldProps;
  // if (!isReviewContext) {
  //   // poiIssueStateStateId, poiIdIssueStateDl, poiIdDocument
  //   console.log(fieldProps.key, isReviewPage);
  // }
  const isConditionalField = Boolean(conditional?.when);
  const isMainFormRef =
    !datagridFormState ||
    (datagridFormState && properties && properties.reference === "form");
  const isMainFormRefForDisable =
    !datagridFormState ||
    (datagridFormState && properties && properties.disableReference === "form");

  let watchFieldValue = useWatch({
    name: getWatchFieldName(fieldProps, datagridFormState),
    disabled: !((conditional && conditional.when) || customConditional),
    control: isMainFormRef ? formControl : formMethods?.control,
  });

  const isDocumentField = type === "document";

  // conditionalRequired support
  const { conditionalRequired, watchKeys: crWatchKeys } = useMemo(
    () => getConditionalRequiredConfig(fieldProps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldProps.key]
  );
  const hasConditionalReqConfig = crWatchKeys.length > 0;

  const crWatchValues = useWatch({
    name: hasConditionalReqConfig ? crWatchKeys : undefined,
    disabled: !hasConditionalReqConfig,
    control: isMainFormRef ? formControl : formMethods?.control,
  });

  // useForm hook reference
  const register = datagridFormState ? formMethods.register : formRegister;
  const control = datagridFormState ? formMethods.control : formControl;
  const getValues = datagridFormState ? formMethods.getValues : formGetValues;
  const trigger = datagridFormState ? formMethods.trigger : formTrigger;
  const setValue = datagridFormState ? formMethods.setValue : formSetValue;
  const getFieldState = datagridFormState
    ? formMethods.getFieldState
    : formGetFieldState;

  if (conditional && watchFieldValue === undefined && !isDocumentField) {
    if (
      formData[conditional.when] !== undefined &&
      (!datagridFormState ||
        (datagridFormState && properties && properties.reference === "form"))
    ) {
      if (conditional.when && conditional.when.includes(".")) {
        watchFieldValue = formData[conditional.when.split(".")[0]];
      } else {
        watchFieldValue = formData[conditional.when];
      }
    }

    if (
      datagridFormState &&
      datagridFormState.record[getDgWatchFieldName(conditional)] !==
        undefined &&
      (!properties || properties.reference !== "form")
    ) {
      watchFieldValue =
        datagridFormState.record[getDgWatchFieldName(conditional)];
    }
  }

  if (isDocumentField) {
    if (
      isConditionalField &&
      Array.isArray(watchFieldValue) &&
      watchFieldValue.every((item) => item === undefined)
    ) {
      if (isMainFormRef) {
        watchFieldValue = formData;
      } else watchFieldValue = datagridFormState.record;
    } else if (isConditionalField && Array.isArray(watchFieldValue)) {
      const watchFieldName = getWatchFieldName(fieldProps, datagridFormState);
      // useWatch values will be undefined when controlling field is not registered yet
      const hasUndefinedValue = watchFieldValue.some(
        (val) => val === undefined
      );
      const defaultWatchValues = {};
      if (hasUndefinedValue) {
        const controllingFieldKeys = Array.isArray(watchFieldName)
          ? watchFieldName
          : [watchFieldName];
        controllingFieldKeys.forEach((field, i) => {
          if (watchFieldValue[i] === undefined) {
            if (isMainFormRef && formData[field] !== undefined) {
              defaultWatchValues[field] = formData[field];
            } else if (
              !isMainFormRef &&
              datagridFormState.record[field] !== undefined
            )
              defaultWatchValues[field] = datagridFormState.record[field];
          }
        });
      }
      if (isMainFormRef)
        watchFieldValue = formConfig?.readOnly
          ? formData
          : { ...formGetValues(), ...defaultWatchValues };
      else
        watchFieldValue = { ...formMethods.getValues(), ...defaultWatchValues };
    }
  }

  const shouldDisplay = () => {
    const _getValues = isMainFormRef ? formGetValues : formMethods.getValues;
    if (isDocumentField)
      return getDocumentVisibility(watchFieldValue, fieldProps);
    return formConditional(fieldProps, watchFieldValue, _getValues());
  };

  // checking whether it's datagrid field or not
  const newFormProps = datagridFormState
    ? { ...datagridFormState.record, applicationData }
    : { ...formData, applicationData };

  // to check whether readOnly or editable field
  const isEditableField =
    (!formConfig.readOnly &&
      !isReviewContext &&
      datagridFormState &&
      datagridFormState.isEditable) ||
    (!formConfig.readOnly && !isReviewContext && !datagridFormState);

  // to watch controlling fields to disable/enable the field
  const watchControllingFieldValues = useWatch({
    name: controllingFieldList,
    control: isMainFormRefForDisable ? formControl : formMethods?.control,
    disabled: controllingFieldList === undefined,
  });
  // to update the disable state when controlling field's value changes
  useEffect(() => {
    const shouldDisable = (fieldValue) => {
      let defaultWatchValues = [];
      if (!Array.isArray(fieldValue) && controllingFieldList) {
        const _getValues = isMainFormRefForDisable
          ? formGetValues
          : formMethods.getValues;
        defaultWatchValues = _getValues(fieldPropsCopy.controllingFieldKey);
      }
      const result = conditionalDisable(
        fieldValue,
        fieldPropsCopy,
        controllingFieldList,
        defaultWatchValues
      );
      return result;
    };
    if (
      (Array.isArray(watchControllingFieldValues) &&
        fieldPropsCopy &&
        fieldPropsCopy.controllingFieldValue) ||
      (!Array.isArray(watchControllingFieldValues) && controllingFieldList)
    ) {
      const res = shouldDisable(watchControllingFieldValues);
      setDisabled(res);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchControllingFieldValues, fieldPropsCopy, controllingFieldList]);

  // for setting initial disable state and to watch for the controlling fields if available
  const savedDgSiblingIds = dgSavedIds[datagridFormState?.datagridKey] || [];
  const dgIdsRenderingKey = savedDgSiblingIds.join(",");
  useEffect(() => {
    if (isEditableField || !statusList.includes(applicationData.status)) {
      const isConditionalDisableItem = disableCheck(fieldProps);
      if (isConditionalDisableItem) {
        const referenceData =
          fieldProps.properties?.disableReference === "form"
            ? { ...formData, applicationData }
            : newFormProps;
        const updatedFieldProps = disableInput(
          fieldProps,
          referenceData,
          datagridFormState,
          dgSavedIds
        );
        if (updatedFieldProps.disabled !== undefined) {
          setDisabled(updatedFieldProps.disabled);
        }
        if (updatedFieldProps.controllingFieldKey) {
          setFieldPropsCopy(updatedFieldProps);
          setControllingFieldList(updatedFieldProps.controllingFieldKey);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dgIdsRenderingKey]);

  // reset fields
  const resetHandler = () => {
    if (fieldProps.validate && fieldProps.validate.custom) {
      const { key } = fieldProps;
      const condition = getParsedValue(fieldProps.validate.custom, key);
      if (condition && condition.reset) {
        const payload = { condition, setValue, trigger, key, getValues };
        dispatch(resetFormData(payload));
      }
    }
  };

  const onOptionListChange = (value) => {
    const result = optionListChange(fieldProps, setValue, value);
    if (result) {
      dispatch(
        showNotification({
          type: "success",
          msg: "Information updated successfully.",
          show: true,
        })
      );
    }
  };

  // Resolve conditionalRequired
  const isConditionallyRequired = useMemo(() => {
    if (!conditionalRequired) return null;
    const _getValues = isMainFormRef ? formGetValues : formMethods?.getValues;
    const currentValues = _getValues ? _getValues() : {};
    const merged = { ...formData, ...currentValues };

    // Apply watched values with fallback to formData defaults
    if (hasConditionalReqConfig) {
      if (Array.isArray(crWatchValues)) {
        crWatchKeys.forEach((key, i) => {
          if (crWatchValues[i] !== undefined) merged[key] = crWatchValues[i];
          else if (formData[key] !== undefined) merged[key] = formData[key];
        });
      } else if (crWatchValues !== undefined && crWatchKeys.length === 1) {
        merged[crWatchKeys[0]] = crWatchValues;
      } else if (
        crWatchKeys.length === 1 &&
        formData[crWatchKeys[0]] !== undefined
      ) {
        merged[crWatchKeys[0]] = formData[crWatchKeys[0]];
      }
    }

    return evaluateConditionalRequired(merged, conditionalRequired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crWatchValues, conditionalRequired]);

  useEffect(() => {
    if (isConditionallyRequired === null) return;
    if (getFieldState(fieldProps.key).error) {
      trigger(fieldProps.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConditionallyRequired]);

  const showField = shouldDisplay();

  useLayoutEffect(() => {
    if (showField) {
      dispatch(
        updateFormFields({
          type: "add",
          field: fieldProps,
          datagridKey: datagridFormState?.datagridKey,
          recordId: datagridFormState?.record?.id,
        })
      );
    }
    return () => {
      dispatch(
        updateFormFields({
          type: "delete",
          field: fieldProps,
          datagridKey: datagridFormState?.datagridKey,
          recordId: datagridFormState?.record?.id,
        })
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showField]);

  if (showField) {
    if (isDocumentField) {
      // Resolve optional dynamically based on conditionalRequired
      const resolvedOptional =
        isConditionallyRequired !== null
          ? !isConditionallyRequired // conditionalRequired=true → optional=false
          : fieldProps.optional;

      return (
        <FormDocumentField
          formProps={newFormProps}
          fieldProps={{
            ...fieldProps,
            optional: resolvedOptional,
            isFirstItem: index === 0,
            isLastItem: index === length,
          }}
        />
      );
    }

    if (fieldProps.type === "readOnlyMapping") {
      return (
        <FormReadOnlyMapping
          control={control}
          trigger={trigger}
          setValue={setValue}
          fieldProps={fieldProps}
          getValues={getValues}
          register={register}
          newFormProps={newFormProps}
        />
      );
    }
    // Resolve validate.required dynamically based on conditionalRequired
    const resolvedFieldProps =
      isConditionallyRequired !== null
        ? {
            ...fieldProps,
            validate: {
              ...fieldProps.validate,
              required: isConditionallyRequired,
            },
          }
        : fieldProps;
    if (isEditableField) {
      return (
        <FormEditableField
          fieldProps={resolvedFieldProps}
          formProps={newFormProps}
          control={control}
          register={register}
          getValues={getValues}
          disabled={disabled}
          trigger={trigger}
          resetHandler={resetHandler}
          onOptionListChange={onOptionListChange}
          setValue={setValue}
          datagridFormState={datagridFormState}
          getFieldState={getFieldState}
        />
      );
    }

    if (fieldProps.type === "note")
      return <FormInfoField fieldProps={fieldProps} />;

    if (fieldProps.type !== "info" && fieldProps.type !== "button") {
      return (
        <FormReadOnlyField
          fieldProps={resolvedFieldProps}
          hasConditionalRequired={isConditionallyRequired !== null}
          newFormProps={newFormProps}
          getValues={getValues}
          setValue={setValue}
          register={register}
          control={control}
          trigger={trigger}
        />
      );
    }
  }
  return "";
}
Field.propTypes = {
  fieldProps: PropTypes.shape({
    conditional: PropTypes.shape({
      when: PropTypes.string.isRequired,
      eq: PropTypes.string.isRequired,
      show: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    }),
    properties: PropTypes.shape({
      reference: PropTypes.string,
      disableReference: PropTypes.string,
    }),
    type: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    validate: PropTypes.shape({
      required: PropTypes.bool,
      custom: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    }),
  }).isRequired,
};
export default Field;
